import { cx } from 'class-variance-authority'
import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useState, useRef, useCallback, memo } from 'react'
import { type FilterAggregates } from '#app/entities/car/service.server.js'
import { Icon } from '#app/shared/ui/icons/Icon.js'
import { Button } from '#app/shared/ui/inputs/button/Button.tsx'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import { type EnumObjectValues } from '../../../../../types/enums'
import { FilterCarroceria } from './FilterCarroceria'
import { FilterCombustible } from './FilterCombustible'
import { FilterKmAnos } from './FilterKmAnos'
import { FilterMarca } from './FilterMarca'
import { FilterPotenciaCambio } from './FilterPotenciaCambio'
import { FilterPrecioCuota } from './FilterPrecioCuota'
import { FilterModelo } from './FiltersModelo'

const FilterValues = {
  MARCA: 'Marca',
  CARROCERIA: 'Carrocería',
  MODELO: 'Modelo',
  PRECIO_Y_CUOTA: 'Precio y cuota',
  COMBUSTIBLE: 'Combustible',
  KM_Y_ANOS: 'Km y años',
  POTENCIA_Y_CAMBIO: 'Potencia y cambio',
} as const

type FilterValuesType = EnumObjectValues<typeof FilterValues>

interface FiltersNavProps {
  aggregates: FilterAggregates
  isMobile: boolean
  maxMileage?: number
  minYear?: number
}

const filterContentAnimations = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2, ease: 'easeInOut' },
}

const mobileOverlayAnimations = {
  initial: { opacity: 0, y: '100%' },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: '100%' },
  transition: { type: 'tween', duration: 0.3 },
}

const MemoizedFilterMarca = memo(FilterMarca)
const MemoizedFilterCarroceria = memo(FilterCarroceria)
const MemoizedFilterModelo = memo(FilterModelo)
const MemoizedFilterPrecioCuota = memo(FilterPrecioCuota)
const MemoizedFilterCombustible = memo(FilterCombustible)
const MemoizedFilterKmAnos = memo(FilterKmAnos)
const MemoizedFilterPotenciaCambio = memo(FilterPotenciaCambio)

interface FilterContentProps {
  filter: FilterValuesType
  aggregates: FilterAggregates
  onActivate?: () => void
  buttonRef?: React.RefObject<HTMLButtonElement>
  isMobile?: boolean
  maxMileage?: number
  minYear?: number
}

const noop = () => {}

const FilterContent = memo(
  ({
    filter,
    aggregates,
    onActivate = noop,
    buttonRef,
    isMobile = false,
    maxMileage,
    minYear,
  }: FilterContentProps) => {
    const internalRef = useRef<HTMLButtonElement>(null)
    const finalRef = buttonRef || internalRef

    switch (filter) {
      case FilterValues.MARCA:
        return (
          <MemoizedFilterMarca
            brandCounts={aggregates.brandCounts}
            isMobile={isMobile}
          />
        )
      case FilterValues.CARROCERIA:
        return (
          <MemoizedFilterCarroceria
            categoryCounts={aggregates.categoryCounts}
            colorCounts={aggregates.colorCounts}
            doorsCounts={aggregates.doorsCounts}
            seatsCounts={aggregates.seatsCounts}
          />
        )
      case FilterValues.MODELO:
        return (
          <MemoizedFilterModelo
            modelCountsByBrand={aggregates.modelCountsByBrand}
            onActivate={onActivate}
            buttonRef={finalRef}
          />
        )
      case FilterValues.PRECIO_Y_CUOTA:
        return (
          <MemoizedFilterPrecioCuota
            deducibleIvaCount={aggregates.deducibleIvaCount}
          />
        )
      case FilterValues.COMBUSTIBLE:
        return (
          <MemoizedFilterCombustible
            fuelTypeCounts={aggregates.fuelTypeCounts}
            ecoLabelCounts={aggregates.ecoLabelCounts}
          />
        )
      case FilterValues.KM_Y_ANOS:
        return (
          <MemoizedFilterKmAnos maxMileage={maxMileage} minYear={minYear} />
        )
      case FilterValues.POTENCIA_Y_CAMBIO:
        return (
          <MemoizedFilterPotenciaCambio
            transmissionCounts={aggregates.transmissionCounts}
            tractionCounts={aggregates.tractionCounts}
          />
        )
      default:
        return null
    }
  },
)

FilterContent.displayName = 'FilterContent'

interface MobileFilterContentProps {
  isOpen: boolean
  onClose: () => void
  selectedFilter: FilterValuesType | null
  aggregates: FilterAggregates
  onFilterSelect: (filter: FilterValuesType | null) => void
  maxMileage?: number
  minYear?: number
}

const MobileFilterContent = memo(
  ({
    isOpen,
    onClose,
    selectedFilter,
    aggregates,
    onFilterSelect,
    maxMileage,
    minYear,
  }: MobileFilterContentProps) => {
    const dummyRef = useRef<HTMLButtonElement>(null)

    const handleFilterClick = useCallback(
      (filter: FilterValuesType) => {
        onFilterSelect(selectedFilter === filter ? null : filter)
      },
      [selectedFilter, onFilterSelect],
    )

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...mobileOverlayAnimations}
            className="fixed inset-0 z-[999] flex flex-col bg-white"
            style={{ height: '100dvh' }}
          >
            <div
              className={`
                flex h-14 items-center justify-between border-b
                border-neutral-100 px-4
              `}
            >
              <span className="text-h4">Filtros</span>
              <button onClick={onClose}>
                <Icon name="basic/x" className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="flex flex-col">
                {Object.entries(FilterValues).map(([key, filter]) => (
                  <div key={key} className="border-b border-neutral-100">
                    <button
                      onClick={() =>
                        handleFilterClick(filter as FilterValuesType)
                      }
                      className={`
                        flex w-full items-center justify-between px-4 py-4
                      `}
                    >
                      <h5 className="text-h5-bold">{filter}</h5>
                      <Icon
                        name="basic/chevron-down"
                        className={cx(
                          'h-5 w-5 text-neutral-400 transition duration-200',
                          selectedFilter === filter && 'rotate-180',
                        )}
                      />
                    </button>

                    <AnimatePresence>
                      {selectedFilter === filter && (
                        <motion.div
                          {...filterContentAnimations}
                          className={`overflow-hidden`}
                        >
                          <div className="px-4">
                            <FilterContent
                              filter={filter as FilterValuesType}
                              aggregates={aggregates}
                              onActivate={() =>
                                handleFilterClick(FilterValues.MARCA)
                              }
                              buttonRef={dummyRef}
                              isMobile={true}
                              maxMileage={maxMileage}
                              minYear={minYear}
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>

            <div className="sticky bottom-0 mt-auto bg-white p-4">
              <Button onClick={onClose} className="w-full py-3 text-white">
                Aplicar filtros
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  },
)

MobileFilterContent.displayName = 'MobileFilterContent'

interface FilterMenuItemProps {
  item: {
    value: FilterValuesType
    trigger: React.ReactNode
  }
  isActive: boolean
  onClick: (e: React.MouseEvent) => void
}

const FilterMenuItem = memo(
  ({ item, isActive, onClick }: FilterMenuItemProps) => (
    <button
      data-value={item.value}
      onClick={onClick}
      className={cx(
        `
          relative flex h-[45px] gap-1.5 border-r border-gray-200 px-4 py-2
          transition-colors

          hover:bg-neutral-50

          last:border-r-0
        `,
      )}
    >
      {item.trigger}
      <Icon
        name="basic/chevron-down"
        className={cx('transition duration-200', isActive && 'rotate-180')}
      />
      {isActive && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary-500" />
      )}
    </button>
  ),
)

FilterMenuItem.displayName = 'FilterMenuItem'

export const FiltersNav = memo(
  ({ aggregates, isMobile, maxMileage, minYear }: FiltersNavProps) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [selectedFilter, setSelectedFilter] =
      useState<FilterValuesType | null>(null)
    const [activeItem, setActiveItem] = useState<FilterValuesType | null>(null)

    const filterRef = useRef<HTMLDivElement>(null)
    const filterContentRef = useRef<HTMLDivElement>(null)
    const filterModeloButtonRef = useRef<HTMLButtonElement>(null)

    const handleMobileFilterClick = useCallback(() => {
      setIsFilterOpen(true)
    }, [])

    const handleCloseMobileFilter = useCallback(() => {
      setIsFilterOpen(false)
      setSelectedFilter(null)
    }, [])

    const handleFilterSelect = useCallback(
      (filter: FilterValuesType | null) => {
        setSelectedFilter(filter)
      },
      [],
    )

    const handleItemClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation()
      const value = (e.currentTarget as HTMLButtonElement).dataset
        .value as FilterValuesType
      setActiveItem((prev) => (prev === value ? null : value))
    }, [])

    useEffect(() => {
      if (isMobile) return

      const handleClickOutside = (event: MouseEvent) => {
        if (
          filterRef.current &&
          !filterRef.current.contains(event.target as Node) &&
          filterContentRef.current &&
          !filterContentRef.current.contains(event.target as Node)
        ) {
          setActiveItem(null)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isMobile])

    if (isMobile) {
      return (
        <>
          <button
            onClick={handleMobileFilterClick}
            className={`
              flex h-[45px] w-full items-center justify-center border
              border-neutral-100 bg-white px-4 py-3
            `}
          >
            <img
              src="/img/filter-lines.svg"
              alt="Filtros"
              className="mr-2 h-4 w-4"
            />
            <span className="text-base font-medium">Filtros</span>
          </button>
          <MobileFilterContent
            isOpen={isFilterOpen}
            onClose={handleCloseMobileFilter}
            selectedFilter={selectedFilter}
            aggregates={aggregates}
            onFilterSelect={handleFilterSelect}
            maxMileage={maxMileage}
            minYear={minYear}
          />
        </>
      )
    }

    const filterMenuItems = Object.values(FilterValues).map((value) => ({
      value,
      trigger: (
        <div className="flex h-full items-center justify-center text-subtitle1-bold">
          {value}
        </div>
      ),
    }))

    return (
      <div
        className={`
          hidden

          lg:block
        `}
      >
        <nav
          className={`
            sticky top-0 z-30 w-full border-y border-gray-200 bg-white
          `}
        >
          <MainContainer>
            <section className="relative flex h-11" ref={filterRef}>
              {filterMenuItems.map((item) => (
                <FilterMenuItem
                  key={item.value}
                  item={item}
                  isActive={activeItem === item.value}
                  onClick={handleItemClick}
                />
              ))}
            </section>
          </MainContainer>

          <AnimatePresence>
            {activeItem && (
              <motion.div
                {...filterContentAnimations}
                className={`
                  absolute top-full z-40 w-full origin-top border-t
                  border-gray-200 bg-white shadow-2xl
                `}
                ref={filterContentRef}
                data-filters-content
              >
                <section className="relative px-14 py-6">
                  <FilterContent
                    filter={activeItem}
                    aggregates={aggregates}
                    onActivate={() => setActiveItem(FilterValues.MARCA)}
                    buttonRef={filterModeloButtonRef}
                    maxMileage={maxMileage}
                    minYear={minYear}
                  />
                </section>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </div>
    )
  },
)
